import React from 'react';
import {
  Button, Col, Form, Input, InputNumber, Radio, Row, Select, Space, Typography, Upload,
} from 'antd';
import { DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import RichTextEditor from './RichTextEditor';
import { useI18n } from '../i18n';

const { Option } = Select;
const { Text } = Typography;

/**
 * Inquiry_Item form fields (inquiry-module-redesign): the same fields the
 * "Add Product" form has always captured (description, target price,
 * branding, special requirements, re-order, attachments) — only the
 * Product AutoComplete itself is gone, replaced by Category as the sole
 * item identifier. Specific product/service/logistics choices are made
 * later in the Category Details page's Selection Wizard.
 */
export default function InquiryItemCategoryFields({
  value,
  onChange,
  categories = [],
  errors = {},
}) {
  const { t } = useI18n();
  const activeCategories = categories.filter((c) => c.status !== 'Inactive');

  const handleCategoryChange = (categoryId) => {
    const category = activeCategories.find((c) => c.id === categoryId);
    onChange('categoryId', categoryId);
    onChange('categoryName', category?.name || '');
  };

  return (
    <>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('Category')}
            required
            validateStatus={errors.categoryId ? 'error' : ''}
            help={errors.categoryId ? t(errors.categoryId) : undefined}
          >
            <Select
              placeholder={t('Category')}
              value={value.categoryId}
              onChange={handleCategoryChange}
              showSearch
              optionFilterProp="children"
            >
              {activeCategories.map((c) => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('Qty')}
            required
            validateStatus={errors.quantity ? 'error' : ''}
            help={errors.quantity ? t(errors.quantity) : undefined}
          >
            <InputNumber
              min={1}
              value={value.quantity}
              onChange={(v) => onChange('quantity', v)}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} sm={16}>
          <Form.Item
            label={t('Product Description')}
            required
            validateStatus={errors.specifications ? 'error' : ''}
            help={errors.specifications ? t(errors.specifications) : undefined}
          >
            <Input
              placeholder={t('Brief description of the product')}
              value={value.specifications}
              onChange={(e) => onChange('specifications', e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Target Price (DKK)')}>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={value.targetPrice}
              onChange={(v) => onChange('targetPrice', v)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item label={t('Branding')}>
            <RichTextEditor
              value={value.brandingDescription}
              onChange={(html) => onChange('brandingDescription', html)}
              placeholder={t('Branding instructions')}
              minHeight={70}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={t('Special Requirements')}>
            <RichTextEditor
              value={value.deliveryRequirements}
              onChange={(html) => onChange('deliveryRequirements', html)}
              placeholder={t('Any special requirements for this product')}
              minHeight={70}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Re-order')}>
            <Radio.Group value={value.reorder} onChange={(e) => onChange('reorder', e.target.value)}>
              <Radio value>{t('Yes')}</Radio>
              <Radio value={false}>{t('No')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label={t('Attachment')}>
        {value.files?.length > 0 && (
          <Space direction="vertical" size={2} style={{ marginBottom: 8, width: '100%' }}>
            {value.files.map((file) => (
              <Space key={file.uid} size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space size={6}>
                  <FileOutlined />
                  <Text>{file.name}</Text>
                </Space>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onChange('files', value.files.filter((f) => f.uid !== file.uid))}
                />
              </Space>
            ))}
          </Space>
        )}
        <Upload
          multiple
          showUploadList={false}
          beforeUpload={(file) => {
            onChange('files', [
              ...(value.files || []),
              { uid: `${Date.now()}-${file.name}`, name: file.name, status: 'done', originFileObj: file },
            ]);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />}>{t('Attach files')}</Button>
        </Upload>
      </Form.Item>
    </>
  );
}
